import { useState } from "react";
import {
  Building2, Plus, Mail, X, Loader2, Copy, Check, Crown, User as UserIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListMyPractices,
  useCreatePractice,
  useListPracticeMembers,
  useInvitePracticeMember,
  useRemovePracticeMember,
  getListMyPracticesQueryKey,
  getListPracticeMembersQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { PhysioLayout } from "@/components/layout/physio-layout";
import { OTLayout } from "@/components/layout/ot-layout";

type Role = "nurse" | "doctor" | "pharmacist" | "physio" | "ot";

function getRole(): Role {
  return ((localStorage.getItem("rheumhub_role") as Role) ?? "nurse");
}

function PortalLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const role = getRole();
  switch (role) {
    case "doctor": return <DoctorLayout title={title}>{children}</DoctorLayout>;
    case "pharmacist": return <PharmacistLayout title={title}>{children}</PharmacistLayout>;
    case "physio": return <PhysioLayout title={title}>{children}</PhysioLayout>;
    case "ot": return <OTLayout title={title}>{children}</OTLayout>;
    default: return <NurseLayout title={title}>{children}</NurseLayout>;
  }
}

function MembersSection({ practiceId, isLead }: { practiceId: number; isLead: boolean }) {
  const queryClient = useQueryClient();
  const { data: members, isLoading } = useListPracticeMembers(practiceId);
  const invite = useInvitePracticeMember();
  const remove = useRemovePracticeMember();
  const [inviteEmail, setInviteEmail] = useState("");
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const handleInvite = async () => {
    if (!inviteEmail.includes("@")) return;
    await invite.mutateAsync({ id: practiceId, data: { email: inviteEmail } });
    setInviteEmail("");
    await queryClient.invalidateQueries({ queryKey: getListPracticeMembersQueryKey(practiceId) });
  };

  const handleRemove = async (membershipId: number) => {
    if (!confirm("Remove this member from the practice?")) return;
    await remove.mutateAsync({ id: practiceId, membershipId });
    await queryClient.invalidateQueries({ queryKey: getListPracticeMembersQueryKey(practiceId) });
  };

  const copyInviteLink = async (token: string) => {
    const link = `${window.location.origin}/practices/join/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4">
      {isLead && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Invite a colleague</Label>
          <div className="mt-2 flex gap-2">
            <Input
              type="email"
              placeholder="colleague@nhs.net"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleInvite}
              disabled={!inviteEmail.includes("@") || invite.isPending}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {invite.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
              Send invite
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            We'll generate a unique invite link. Share it directly with your colleague — email delivery comes in a future release.
          </p>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading members…</p>
      ) : (members ?? []).length === 0 ? (
        <p className="text-sm text-slate-400">No members yet.</p>
      ) : (
        <div className="space-y-2">
          {(members ?? []).map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                {m.membershipRole === "lead"
                  ? <Crown className="h-4 w-4 text-amber-600" />
                  : <UserIcon className="h-4 w-4 text-slate-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {m.displayName ?? m.email ?? m.invitedEmail ?? "Unknown member"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {m.membershipRole === "lead" ? "Practice lead" : "Member"}
                  {m.status === "invited" && " · Invite pending"}
                  {m.email && m.displayName && ` · ${m.email}`}
                </p>
              </div>
              {m.status === "invited" && m.inviteToken && (
                <button
                  onClick={() => copyInviteLink(m.inviteToken!)}
                  className="text-xs text-teal-700 hover:text-teal-800 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-teal-50 transition-colors"
                  title="Copy invite link"
                >
                  {copiedToken === m.inviteToken
                    ? <><Check className="h-3.5 w-3.5" /> Copied</>
                    : <><Copy className="h-3.5 w-3.5" /> Copy link</>}
                </button>
              )}
              {isLead && (
                <button
                  onClick={() => handleRemove(m.id)}
                  className="text-slate-400 hover:text-red-600 transition-colors"
                  title="Remove member"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HcpPractices() {
  const queryClient = useQueryClient();
  const { data: practices, isLoading } = useListMyPractices();
  const createPractice = useCreatePractice();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"nhs" | "private" | "independent">("nhs");

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await createPractice.mutateAsync({ data: { name: newName.trim(), practiceType: newType } });
    setNewName("");
    setShowCreate(false);
    await queryClient.invalidateQueries({ queryKey: getListMyPracticesQueryKey() });
  };

  return (
    <PortalLayout title="Practices">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Your practices</h2>
            <p className="text-sm text-slate-500 mt-1">
              Switch context between organisations. Patient lists, advice queues, and team inboxes are scoped to the active practice.
              Your CPD and revalidation records belong to you and stay the same across practices.
            </p>
          </div>
          <Button
            onClick={() => setShowCreate((s) => !s)}
            className="bg-teal-700 hover:bg-teal-800 text-white shrink-0"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New practice
          </Button>
        </div>

        {showCreate && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <Label className="text-sm font-medium text-slate-700">Practice name</Label>
            <Input
              placeholder="e.g. Leeds Teaching Hospitals — Rheumatology"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Label className="text-sm font-medium text-slate-700">Practice type</Label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as "nhs" | "private" | "independent")}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="nhs">NHS trust</option>
              <option value="private">Private clinic</option>
              <option value="independent">Independent practice</option>
            </select>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || createPractice.isPending}
                className="bg-teal-700 hover:bg-teal-800 text-white"
              >
                {createPractice.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create practice"}
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-slate-400">Loading practices…</p>
        ) : (practices ?? []).length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">You're not part of any practice yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              Create a practice or ask a colleague to send you an invite link.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(practices ?? []).map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-teal-700" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{p.name}</h3>
                        {p.isActive && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Active</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 capitalize">
                        {p.practiceType} · You are {p.membershipRole}
                        {p.membershipStatus === "invited" && " · Invitation pending"}
                      </p>
                    </div>
                  </div>
                </div>
                <MembersSection practiceId={p.id} isLead={p.membershipRole === "lead"} />
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
